import { format, formatDistanceToNow } from 'date-fns';
import { X, AlertTriangle, AlertOctagon, ArrowRight, Clock } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type MappedAssetSensor, sensorStatusLabels } from '@/common/asset-sensor';
import { Button } from '@/components/ui/button';
import { getLanguage } from '@/i18n';
import { datePatternsFor } from '@/i18n/format';
import { getDateFnsLocale } from '@/lib/utils';

interface SensorChartOverlayProps {
	assetId: string;
	selectedSensor: MappedAssetSensor;
	onClose: () => void;
}

export const SensorChartOverlay: React.FC<SensorChartOverlayProps> = ({
	assetId,
	selectedSensor,
	onClose,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleGoToDetails = () => {
		navigate(`/assets/${assetId}/health?sensorId=${selectedSensor.uniqId}`);
	};

	// Helper to safely format dates
	const getRelativeTime = (dateStr?: string | null) => {
		if (!dateStr) return '-';
		try {
			const date = new Date(dateStr);
			return formatDistanceToNow(date, { addSuffix: true, locale: getDateFnsLocale() });
		} catch {
			return '-';
		}
	};

	const getFormattedDate = (dateStr?: string | null) => {
		if (!dateStr) return '';
		try {
			return format(new Date(dateStr), datePatternsFor(getLanguage()).dateTimeShort, { locale: getDateFnsLocale() });
		} catch {
			return '';
		}
	};

	const lastReadingDate = selectedSensor.lastReading;
	const lastWarnDate = selectedSensor.lastWarnAlarm;
	const hasWarningHistory = Boolean(lastWarnDate);

	const sensorData = selectedSensor as any;
	const numAlarms = sensorData.numAlarms ?? 0;
	const numWarnHigh = sensorData.numWarnHigh ?? 0;
	const numWarnLow = sensorData.numWarnLow ?? 0;

	return (
		<div
			className="absolute top-16 right-4 w-80 bg-card shadow-2xl rounded-xl flex flex-col overflow-hidden z-[100] border border-border animate-in fade-in slide-in-from-right-4 duration-300"
			onMouseDown={(e) => e.stopPropagation()}
			onClick={(e) => e.stopPropagation()}
			onWheel={(e) => e.stopPropagation()}
		>
			{/* Header */}
			<div className="flex items-start justify-between px-4 py-3 bg-card border-b border-foreground/20">
				<div className="flex flex-col overflow-hidden mr-2">
					<span className="font-bold text-foreground text-sm truncate" title={selectedSensor.label}>
						{selectedSensor.label}
					</span>
					<span className="text-xs text-muted-foreground font-mono truncate">
						{selectedSensor.name}
					</span>
				</div>

				<div className="flex items-center gap-2 shrink-0">
					<span
						className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border shadow-sm"
						style={{
							color: selectedSensor.statusSettings.map.statusColor,
							borderColor: selectedSensor.statusSettings.map.borderColor,
							backgroundColor: selectedSensor.statusSettings.map.backgroundColor || '#fff',
						}}
					>
						{sensorStatusLabels(t)[selectedSensor.status]}
					</span>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
						onClick={onClose}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
			</div>

			<div className="flex flex-col divide-y divide-border bg-muted/10">
				<MetricItem
					label={t('viewer.sensor.lastUpdate')}
					value={getRelativeTime(lastReadingDate)}
					icon={<Clock className="w-4 h-4 text-muted-foreground" />}
					isLarge
				/>

				{hasWarningHistory ? (
					<MetricItem
						label={t('viewer.sensor.lastWarnAlarm')}
						value={getRelativeTime(lastWarnDate)}
						subValue={getFormattedDate(lastWarnDate)}
						icon={<AlertTriangle className="w-3.5 h-3.5 text-warning" />}
					/>
				) : (
					<div className="py-8 px-4 text-center bg-transparent">
						<div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
							<div className="p-2 bg-emerald-500/10 rounded-full">
								<AlertTriangle className="w-5 h-5 text-emerald-500" />
							</div>
							<span className="text-sm font-medium text-foreground">{t('viewer.sensor.noWarnings')}</span>
						</div>
					</div>
				)}

				<div className="grid grid-cols-3 divide-x divide-border bg-card border-t border-border">
					<div className="flex flex-col">
						<MetricItem
							label={t('viewer.sensor.activeAlarm')}
							value={numAlarms}
							icon={<AlertOctagon className="w-3.5 h-3.5 text-destructive" />}
							highlight={numAlarms > 0}
							compact
						/>
					</div>
					<div className="flex flex-col">
						<MetricItem
							label={t('viewer.sensor.warnHigh')}
							value={numWarnHigh}
							icon={<AlertTriangle className="w-3.5 h-3.5 text-warning" />}
							compact
						/>
					</div>
					<div className="flex flex-col">
						<MetricItem
							label={t('viewer.sensor.warnLow')}
							value={numWarnLow}
							icon={<AlertTriangle className="w-3.5 h-3.5 text-warning/80" />}
							compact
						/>
					</div>
				</div>
			</div>

			<div className="p-3 bg-card border-t border-border mt-auto">
				<Button
					className="w-full justify-between group shadow-sm"
					variant="default"
					onClick={handleGoToDetails}
				>
					<span className="font-medium">{t('viewer.sensor.analytics')}</span>
					<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
				</Button>
			</div>
		</div>
	);
};

interface MetricItemProps {
	label: string;
	value?: string | number;
	subValue?: string;
	icon?: React.ReactNode;
	highlight?: boolean;
	isLarge?: boolean;
	compact?: boolean;
}

const MetricItem: React.FC<MetricItemProps> = ({
	label,
	value,
	subValue,
	icon,
	highlight,
	isLarge,
	compact,
}) => {
	return (
		<div
			className={`flex flex-col justify-center bg-transparent w-full ${compact ? 'p-3' : 'p-4'}`}
		>
			<div className="flex items-center gap-1.5 mb-1.5">
				{icon}
				<span className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider leading-none">
					{label}
				</span>
			</div>

			<div className="flex items-baseline gap-1">
				<span
					className={`font-bold leading-tight break-all
						${highlight ? 'text-destructive' : 'text-foreground'} 
						${isLarge ? 'text-2xl' : 'text-lg'}
					`}
				>
					{value}
				</span>
			</div>

			{subValue && (
				<div className="text-[11px] text-muted-foreground/80 font-medium mt-1 truncate">
					{subValue}
				</div>
			)}
		</div>
	);
};
